import { apiCall } from '../../../../_services/apiCall'; // Adjust path as needed
import config from '../../../../config/config'; // Adjust path as needed


/* ==================== IOT Meters CRUD ==================== */

/**
 * Get all meters
 */
export const getMeters = async () => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters`,
      {},
      {},
      "GET"
    );
    
    return isSuccess ? data : [];
  } catch (error) {
    console.error('Error fetching meters:', error);
    throw error;
  }
};

/**
 * Get single meter with KPI mappings
 */
export const getMeter = async (meterId) => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/${meterId}`,
      {},
      {},
      "GET"
    );
    
    return isSuccess ? data : null;
  } catch (error) {
    console.error('Error fetching meter:', error);
    throw error;
  }
};

/**
 * Create new meter
 */
export const createMeter = async (meterData) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters`,
      meterData,
      {},
      "POST"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to create meter');
  } catch (error) {
    console.error('Error creating meter:', error);
    throw error;
  }
};

/**
 * Update meter
 */
export const updateMeter = async (meterId, meterData) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/${meterId}`,
      meterData,
      {},
      "PUT"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to update meter');
  } catch (error) {
    console.error('Error updating meter:', error);
    throw error;
  }
};

/**
 * Delete meter
 */
export const deleteMeter = async (meterId) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/${meterId}`,
      {},
      {},
      "DELETE"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to delete meter');
  } catch (error) {
    console.error('Error deleting meter:', error);
    throw error;
  }
};

/* ==================== KPI Management ==================== */

/**
 * Get all KPIs (optionally filtered by type)
 * @param {object} params - { kpiType: 'INTERNAL' | 'EXTERNAL' | undefined }
 */
export const getKPIs = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.kpiType) {
      queryParams.append('kpiType', params.kpiType);
    }
    
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/kpis`, 
      {}, 
      {kpiType: params.kpiType}, 
      "GET"
    );

    return isSuccess ? data : [];
  } catch (error) {
    console.error('Error fetching KPIs:', error);
    throw error;
  }
};

/**
 * Get single KPI by ID
 */
export const getKPI = async (kpiId) => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/kpis/${kpiId}`,
      {},
      {},
      "GET"
    );
    
    return isSuccess ? data : null;
  } catch (error) {
    console.error('Error fetching KPI:', error);
    throw error;
  }
};

/**
 * Create new KPI
 */
export const createKPI = async (kpiData) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/kpis/`,
      {},
      kpiData,
      "POST"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to create KPI');
  } catch (error) {
    console.error('Error creating KPI:', error);
    throw error;
  }
};

/**
 * Delete KPI
 */
export const deleteKPI = async (kpiId) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/kpis/${kpiId}`,
      {},
      {},
      "DELETE"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to delete KPI');
  } catch (error) {
    console.error('Error deleting KPI:', error);
    throw error;
  }
};

/* ==================== Analytics / Graph Data ==================== */

/**
 * Generic function to fetch graph data with custom widget config
 * Used for all meter visualizations
 */
export const getGraphData = async (graphConfig) => {
  let apiUrl = `${config.POSTLOGIN_API_URL_COMPANY}bi-data/preview`;
  try {
    const { isSuccess, data } = await apiCall(
      apiUrl,
      {},
      graphConfig,
      "POST",
      false,
      false
    );
    
    if (isSuccess && data?.data) {
      return {
        data: data.data,
        metadata: data.metadata
      };
    }
    
    return { data: [], metadata: {} };
  } catch (error) {
    console.error('Error fetching graph data:', error);
    throw error;
  }
};

/**
 * Helper to build widget config for bi-graph API
 *
 * Every filter is a list, and a caller may pass one value or several. Wrapping
 * an argument that is already a list gives `[["a","b"]]`, which the graph query
 * tolerated but the insights endpoint does not: it fills each combination of
 * the grouped dimensions from these lists, and a filter holding one value that
 * is itself a list matches no row, so every measured row is replaced by a zero
 * and the graph reports that it has nothing to say.
 */
export const buildWidgetConfig = ({
  meterId,
  kpiId,
  module,
  category,
  subCategory,
  kpi,
  kpiType,
  measurementType,
  financialYear,
  startDateTime,
  endDateTime,
  aggregationPeriodValue = 1,
  aggregationPeriodUnit = 'days',
  locationId = '1',
  title = '',
  chartType = 'line',
  xAxisField = 'displayPeriods',
  stackByField = null
}) => {
  return {
    title,
    dataSourceType: 'IOT_METERS',
    description: '',
    chartType,
    xAxisField,
    stackByField,
    enableStacking: false,
    representationType: 'Graph',
    widgetConfig: {
      module: [module],
      kpiType: kpiType,
      measurementType: measurementType,
      filters: {
        financial_year: [financialYear],
        location_id: locationId ? [locationId] : [],
        sublocation_id: [],
        category: category ? [].concat(category) : [],
        sub_category: subCategory ? [].concat(subCategory) : [],
        kpi: kpi ? [].concat(kpi) : [],
        displayPeriods: [],
        startDateTime,
        endDateTime,
        aggregationPeriodValue,
        aggregationPeriodUnit,
        meter_id: meterId ? (Array.isArray(meterId) ? meterId : [meterId]) : [],
      },
      group_by: stackByField ? [xAxisField, stackByField] : [xAxisField],
      aggregate: {
        sum: 'value'
      },
      sort: {
        field: xAxisField,
        order: 'asc'
      },
      aggregateSubLocations: true
    }
  };
};
/**
 * Alert instances raised against one meter, newest first.
 * @param {string} meterId - The meter to fetch history for.
 * @returns {Promise<Array<object>>} Alert instances, empty when none or on failure.
 */
export const getMeterAlertInstances = async (meterId) => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/alert-instances`,
      {},
      meterId ? { meterId } : {},
      "GET"
    );

    return isSuccess && Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching meter alert instances:', error);
    return [];
  }
};

/**
 * Every alert instance across the fleet, newest first.
 *
 * The endpoint filters on meter only when asked, so omitting it answers the
 * question the downtime report needs — what has failed lately — in one request
 * rather than one per meter.
 *
 * @returns {Promise<Array<object>>} Alert instances, empty when none or on failure.
 */
export const getAllAlertInstances = () => getMeterAlertInstances();

/**
 * Per-day reading counts for a meter, used to show which days it reported.
 * @param {string} meterId - The meter to report on.
 * @param {number} days - How many days back to look.
 * @returns {Promise<{from: string, to: string, days: Array<object>}|null>} Counts,
 *   or null when the request fails.
 */
export const getMeterReadingCounts = async (meterId, days = 30) => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/${meterId}/reading-counts`,
      {},
      { days },
      "GET"
    );

    return isSuccess ? data : null;
  } catch (error) {
    console.error('Error fetching meter reading counts:', error);
    return null;
  }
};
